import api from './api';

export interface MetricLevelInstance {
    id: string;
    metric_level_id: number;
    name: string;
    start_date?: string | null;
    end_date?: string | null;
    parent_instance_id?: string | null;
    metric_level_name?: string | null;
    metric_level_display_label?: string | null;
    is_period_based?: boolean;
    parent_instance_name?: string | null;
}

export interface ApplicableMetricLevel {
    id: number;
    name: string;
    display_label: string;
    is_period_based: boolean;
    level_number: number;
}

export interface MetricLevelConfig {
    schema_version: number;
    applicable_metric_level_ids: number[];
    metric_level_names: string[];
    applicable_metric_levels: ApplicableMetricLevel[];
    instances: MetricLevelInstance[];
}

export interface MetricLevelInstanceInput {
    id?: string;
    metric_level_id: number;
    name: string;
    start_date?: string | null;
    end_date?: string | null;
    parent_instance_id?: string | null;
}

export const getReviewEntityMetricLevelConfig = (entityId: number): Promise<MetricLevelConfig> =>
    api.get(`/review_entities/${entityId}/metric_level_config`).then(res => res.data);

export const updateReviewEntityMetricLevelConfig = (
    entityId: number,
    payload: { metric_level_names: string[]; instances: MetricLevelInstanceInput[] }
): Promise<MetricLevelConfig> =>
    api.put(`/review_entities/${entityId}/metric_level_config`, payload).then(res => res.data);

export const updateReviewEntityApplicableMetricLevels = (
    entityId: number,
    metric_level_names: string[]
): Promise<MetricLevelConfig> =>
    api.put(`/review_entities/${entityId}/metric_level_config/applicable_levels`, {
        metric_level_names
    }).then(res => res.data);

export const createReviewEntityMetricLevelInstance = (
    entityId: number,
    instance: MetricLevelInstanceInput
): Promise<MetricLevelConfig> =>
    api.post(`/review_entities/${entityId}/metric_level_config/instances`, instance).then(res => res.data);

export const updateReviewEntityMetricLevelInstance = (
    entityId: number,
    instanceId: string,
    instance: MetricLevelInstanceInput
): Promise<MetricLevelConfig> =>
    api.put(`/review_entities/${entityId}/metric_level_config/instances/${encodeURIComponent(instanceId)}`, instance)
        .then(res => res.data);

export const deleteReviewEntityMetricLevelInstance = (
    entityId: number,
    instanceId: string
): Promise<MetricLevelConfig> =>
    api.delete(`/review_entities/${entityId}/metric_level_config/instances/${encodeURIComponent(instanceId)}`)
        .then(res => res.data);

const safeDownloadFilenamePart = (value: string): string =>
    value
        .trim()
        .replace(/[^\w.\-]+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '') || 'file';

const parseContentDispositionFilename = (disposition?: string | null): string | null => {
    if (!disposition) return null;
    const utf8Match = disposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
        try {
            return decodeURIComponent(utf8Match[1].trim().replace(/^["']|["']$/g, ''));
        } catch {
            return utf8Match[1].trim().replace(/^["']|["']$/g, '');
        }
    }
    const plainMatch = disposition.match(/filename\s*=\s*"([^"]+)"/i)
        || disposition.match(/filename\s*=\s*([^;]+)/i);
    if (!plainMatch?.[1]) return null;
    return plainMatch[1].trim().replace(/^["']|["']$/g, '');
};

export const downloadReviewEntityMetricsTemplate = async (
    entityId: number,
    metricLevelName: string,
    includeSampleValues: boolean,
    entityName?: string,
    levelInstanceIds?: string[],
    levelInstanceNames?: string[]
): Promise<void> => {
    try {
        const ids = (levelInstanceIds || []).filter(Boolean);
        const names = (levelInstanceNames || []).filter(Boolean);
        const response = await api.get(`/review_entities/${entityId}/metrics-template`, {
            params: {
                metric_level_name: metricLevelName,
                include_sample_values: includeSampleValues,
                ...(ids.length === 1 ? { level_instance_id: ids[0] } : {}),
                ...(ids.length > 1 ? { level_instance_ids: ids.join(',') } : {}),
                ...(ids.length === 0 && names.length
                    ? { level_instance_name: names.join(',') }
                    : {})
            },
            responseType: 'blob'
        });
        const blob =
            response.data instanceof Blob
                ? response.data
                : new Blob([response.data], {
                      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                  });
        if (!blob.size) {
            throw new Error('Empty template response.');
        }
        const headers = response.headers as Record<string, string> & {
            get?: (name: string) => string | undefined;
        };
        const disposition =
            (typeof headers.get === 'function'
                ? headers.get('content-disposition')
                : undefined) ||
            headers['content-disposition'] ||
            headers['Content-Disposition'];
        const mode = includeSampleValues ? 'sample' : 'blank';
        const fallback =
            [
                safeDownloadFilenamePart(entityName || `entity_${entityId}`),
                safeDownloadFilenamePart(metricLevelName),
                ids.length === 1 && names[0]
                    ? safeDownloadFilenamePart(names[0])
                    : ids.length > 1
                      ? `${ids.length}_instances`
                      : metricLevelName.toUpperCase() === 'PROJECT'
                        ? 'all_leaves'
                        : null,
                mode
            ]
                .filter(Boolean)
                .join('_') + '.xlsx';
        const filename = parseContentDispositionFilename(disposition) || fallback;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    } catch (error: unknown) {
        const axiosError = error as { response?: { data?: Blob } };
        if (axiosError.response?.data instanceof Blob) {
            const text = await axiosError.response.data.text();
            try {
                const parsed = JSON.parse(text) as { detail?: unknown };
                const detail = parsed.detail;
                throw new Error(
                    typeof detail === 'string' ? detail : text || 'Failed to download metrics template'
                );
            } catch (parseErr) {
                if (parseErr instanceof Error && parseErr.message !== text) {
                    throw parseErr;
                }
                throw new Error(text || 'Failed to download metrics template');
            }
        }
        throw error;
    }
};
